import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import {
  getAllDesigns,
  createDesign,
  updateDesign,
  uploadDesignVersion,
  getAllBrands,
  getAllRequirements,
  getAllFabricOptions,
  getAllUsers,
} from '../../store/actions';
import Header from '../../components/layout/Header';
import Badge from '../../components/ui/Badge';
import Modal from '../../components/ui/Modal';
import {
  CHARGE_STATUS,
  DESIGN_STATUS,
  DESIGN_VERSION_STATUS,
  FINALISATION_STATUS,
  GARMENT_TYPE,
  formatDate,
  formatINR,
  label,
  options,
  today,
} from '../../constants/garmentCodes';

import useCreateIntent, { CREATE_INTENT } from '../../customHooks/useCreateIntent';
import { uploadFileToMinio } from '../../customHooks/S3Upload';
const STATUS = { AWAITING_CHARGE: 1, IN_PROGRESS: 2, SUBMITTED: 3, REVISION: 4, APPROVED: 5, FINALISED: 6 };
const CHARGE = { PENDING: 1, APPROVED: 2 };
const FINAL = { FINALISED: 4 };
const inputCls = 'w-full text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:border-blue-400';

const latestVersion = (design) => {
  const versions = design?.versions || [];
  return versions.length ? versions[versions.length - 1] : null;
};

function RevisionBar({ used = 0, allowed = 0, height = 'h-1.5' }) {
  const pct = allowed ? Math.min(100, Math.round((used / allowed) * 100)) : 0;
  const exhausted = allowed > 0 && used >= allowed;
  return (
    <div className={`w-full bg-gray-100 rounded-full ${height}`}>
      <div className={`${height} rounded-full ${exhausted ? 'bg-red-400' : pct > 60 ? 'bg-amber-400' : 'bg-blue-400'}`} style={{ width: `${pct}%` }} />
    </div>
  );
}

function DesignCard({ design, onClick }) {
  const status = label(DESIGN_STATUS, design.status);
  const image = latestVersion(design)?.frontImage;
  const exhausted = design.revisionAllowance > 0 && design.revisionsUsed >= design.revisionAllowance;
  return (
    <button onClick={onClick} className={`bg-white rounded-lg border border-gray-100 overflow-hidden hover:shadow-md transition-shadow text-left group ${design.__optimistic ? 'opacity-60' : ''}`}>
      <div className="aspect-[3/4] bg-gray-50 overflow-hidden relative">
        {image ? (
          <img src={image} alt={design.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-gray-300"><span className="text-4xl">◈</span></div>
        )}
        <div className="absolute top-2 left-2"><Badge status={status} /></div>
        {design.currentVersion > 0 && (
          <div className="absolute top-2 right-2 bg-black/60 text-white text-[10px] font-bold px-2 py-1 rounded">V{design.currentVersion}</div>
        )}
      </div>
      <div className="p-3">
        <p className="text-[13px] font-semibold text-gray-900 mb-0.5">{design.name}</p>
        <p className="text-[11px] text-gray-400 mb-2">{design.requirementName || '—'} · {design.brandName || '—'}</p>
        <div className="flex items-center justify-between text-[11px] mb-1">
          <span className="text-gray-500">Revisions</span>
          <span className={`font-medium ${exhausted ? 'text-red-600' : 'text-gray-700'}`}>{design.revisionsUsed ?? 0}/{design.revisionAllowance ?? 0}</span>
        </div>
        <RevisionBar used={design.revisionsUsed} allowed={design.revisionAllowance} />
        {design.charge != null && <p className="text-[11px] text-gray-500 mt-2 font-mono">{formatINR(design.charge)}</p>}
      </div>
    </button>
  );
}

function UploadVersionModal({ open, onClose, designs, initialDesignId }) {
  const dispatch = useDispatch();
  const [form, setForm] = useState({ designId: '', frontImage: '', backImage: '', detailImage: '', revisionSummary: '', designerRemarks: '', versionNumber: '' });
  const [uploadingSlot, setUploadingSlot] = useState(null);

  useEffect(() => {
    if (open) {
      const selectedId = initialDesignId ? String(initialDesignId) : '';
      setForm((f) => ({ ...f, designId: selectedId, frontImage: '', backImage: '', detailImage: '', revisionSummary: '', designerRemarks: '', versionNumber: '' }));
    }
  }, [open, initialDesignId]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const handleImageUpload = (field) => async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setUploadingSlot(field);
    try {
      const url = await uploadFileToMinio(file, 'designs');
      if (url) setForm((f) => ({ ...f, [field]: url }));
    } catch (err) {
      console.error('Design image upload failed:', err);
    } finally {
      setUploadingSlot(null);
    }
  };
  const submit = (e) => {
    e.preventDefault();
    const designId = Number(form.designId);
    dispatch(
      uploadDesignVersion({
        payload: {
          designId,
          uploadedDate: today(),
          status: 1,
          revisionSummary: form.revisionSummary || null,
          frontImage: form.frontImage || null,
          backImage: form.backImage || null,
          detailImage: form.detailImage || null,
        },
        callback: {
          success: () => dispatch(updateDesign({ payload: { id: designId, status: STATUS.SUBMITTED, stage: 7, finalisationStatus: 3 } })),
        },
      }),
    );
    onClose();
  };

  const selectedDesign = designs.find(d => String(d.id) === form.designId);
  const nextVersion = selectedDesign ? `V${(selectedDesign.currentVersion || 0) + 1}` : '';

  return (
    <Modal open={open} onClose={onClose} title="Upload Design Version" size="lg">
      <form onSubmit={submit} className="space-y-4">
        <div className="grid grid-cols-3 gap-3">
          {[
            { type: 'Front view', field: 'frontImage' },
            { type: 'Back view', field: 'backImage' },
            { type: 'Detail shot', field: 'detailImage' },
          ].map(({ type, field }) => (
            <div key={type} className="border-2 border-dashed border-gray-200 rounded-lg p-4 text-center">
              {form[field] ? (
                <img src={form[field]} alt={type} className="w-full aspect-square object-cover rounded mb-2" />
              ) : (
                <p className="text-[11px] text-gray-400 mb-2">{type}</p>
              )}
              <label className="text-[11px] text-blue-600 hover:underline cursor-pointer inline-block">
                {uploadingSlot === field ? 'Uploading…' : form[field] ? 'Replace' : 'Upload'}
                <input
                  type="file"
                  accept="image/*"
                  className="hidden"
                  disabled={uploadingSlot === field}
                  onChange={handleImageUpload(field)}
                />
              </label>
            </div>
          ))}
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-[11px] font-medium text-gray-600 mb-1">Design Name</label>
            <select required value={form.designId} onChange={set('designId')} className={inputCls}>
              <option value="">Select design</option>
              {designs.filter(d => d.finalisationStatus !== FINAL.FINALISED && d.chargeStatus === CHARGE.APPROVED).map((d) => (
                <option key={d.id} value={d.id}>{d.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-[11px] font-medium text-gray-600 mb-1">Version Number</label>
            <input readOnly value={nextVersion} placeholder="V2" className={`${inputCls} font-mono bg-gray-50`} />
          </div>
        </div>
        <div>
          <label className="block text-[11px] font-medium text-gray-600 mb-1">Revision Summary</label>
          <textarea rows={2} value={form.revisionSummary} onChange={set('revisionSummary')} placeholder="What changed in this version?" className={inputCls} />
        </div>
        <div>
          <label className="block text-[11px] font-medium text-gray-600 mb-1">Designer Remarks</label>
          <textarea rows={2} value={form.designerRemarks} onChange={set('designerRemarks')} placeholder="Notes for the brand" className={inputCls} />
        </div>
        <div className="flex gap-3">
          <button type="button" onClick={onClose} className="flex-1 px-4 py-2 text-sm text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50">Cancel</button>
          <button type="submit" className="flex-1 px-4 py-2 text-sm font-medium text-white bg-[#1b3a6b] rounded-lg hover:bg-[#152d55]">Upload & Submit</button>
        </div>
      </form>
    </Modal>
  );
}

function CreateDesignModal({ open, onClose }) {
  const dispatch = useDispatch();
  const requirements = useSelector((state) => state.requirement.requirements);
  const fabrics = useSelector((state) => state.fabricoption.options);
  const users = useSelector((state) => state.user.users);
  const [form, setForm] = useState({ requirementId: '', name: '', garmentType: '', charge: '', revisionAllowance: '2', linkedFabricOptionId: '', designerUserId: '' });
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const num = (v) => (v === '' ? null : Number(v));

  useEffect(() => {
    if (form.requirementId) dispatch(getAllFabricOptions({ requirementId: form.requirementId, decisionStatus: 4 }));
  }, [dispatch, form.requirementId]);

  const submit = (e) => {
    e.preventDefault();
    dispatch(
      createDesign({
        payload: {
          requirementId: Number(form.requirementId),
          name: form.name.trim(),
          garmentType: num(form.garmentType),
          charge: num(form.charge),
          chargeStatus: CHARGE.PENDING,
          currentVersion: 0,
          revisionAllowance: num(form.revisionAllowance),
          revisionsUsed: 0,
          status: STATUS.AWAITING_CHARGE,
          finalisationStatus: 1,
          stage: 5,
          linkedFabricOptionId: num(form.linkedFabricOptionId),
          designerUserId: num(form.designerUserId),
        },
      }),
    );
    setForm({ requirementId: '', name: '', garmentType: '', charge: '', revisionAllowance: '2', linkedFabricOptionId: '', designerUserId: '' });
    onClose();
  };

  return (
    <Modal open={open} onClose={onClose} title="Create Design Request" size="md">
      <form onSubmit={submit} className="space-y-4">
        <div>
          <label className="block text-[11px] font-medium text-gray-600 mb-1">Requirement *</label>
          <select required value={form.requirementId} onChange={set('requirementId')} className={inputCls}>
            <option value="">Select requirement</option>
            {requirements.map((r) => <option key={r.id} value={r.id}>{[r.code, r.name, r.brandName].filter(Boolean).join(' · ')}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-[11px] font-medium text-gray-600 mb-1">Design Name *</label>
          <input required value={form.name} onChange={set('name')} className={inputCls} />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-[11px] font-medium text-gray-600 mb-1">Garment Type</label>
            <select value={form.garmentType} onChange={set('garmentType')} className={inputCls}>
              <option value="">Select type</option>
              {options(GARMENT_TYPE).map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-[11px] font-medium text-gray-600 mb-1">Design Charge (INR) *</label>
            <input required type="number" min="0" value={form.charge} onChange={set('charge')} className={`${inputCls} font-mono`} />
          </div>
          <div>
            <label className="block text-[11px] font-medium text-gray-600 mb-1">Revision Allowance</label>
            <input type="number" min="0" value={form.revisionAllowance} onChange={set('revisionAllowance')} className={`${inputCls} font-mono`} />
          </div>
          <div>
            <label className="block text-[11px] font-medium text-gray-600 mb-1">Designer</label>
            <select value={form.designerUserId} onChange={set('designerUserId')} className={inputCls}>
              <option value="">Unassigned</option>
              {users.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
            </select>
          </div>
          <div className="col-span-2">
            <label className="block text-[11px] font-medium text-gray-600 mb-1">Approved Fabric</label>
            <select value={form.linkedFabricOptionId} onChange={set('linkedFabricOptionId')} disabled={!form.requirementId} className={inputCls}>
              <option value="">None</option>
              {form.requirementId && fabrics.map((f) => <option key={f.id} value={f.id}>{f.name}</option>)}
            </select>
          </div>
        </div>
        <div className="flex gap-3 pt-2">
          <button type="button" onClick={onClose} className="flex-1 px-4 py-2 text-sm text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50">Cancel</button>
          <button type="submit" className="flex-1 px-4 py-2 text-sm font-medium text-white bg-[#1b3a6b] rounded-lg hover:bg-[#152d55]">Create Design Request</button>
        </div>
      </form>
    </Modal>
  );
}

export default function Designs() {
  const dispatch = useDispatch();
  const [params] = useSearchParams();
  const requirementId = params.get('requirementId') || '';
  const [selected, setSelected] = useState(null);
  const [uploadFor, setUploadFor] = useState(undefined);
  const [createOpen, setCreateOpen] = useState(false);
  const [brandFilter, setBrandFilter] = useState('');

  const designs = useSelector((state) => state.design.designs);
  const loading = useSelector((state) => state.design.loading);
  const brands = useSelector((state) => state.brand.brands);

  useEffect(() => {
    dispatch(getAllBrands());
  }, [dispatch]);

  useEffect(() => {
    dispatch(getAllDesigns({ brandId: brandFilter, status: '', requirementId }));
  }, [dispatch, brandFilter, requirementId]);

  const stats = designs.reduce(
    (acc, d) => {
      acc.total += 1;
      if (d.status === STATUS.IN_PROGRESS) acc.inProgress += 1;
      if (d.status === STATUS.SUBMITTED || d.status === STATUS.AWAITING_CHARGE) acc.awaiting += 1;
      if (d.finalisationStatus === FINAL.FINALISED) acc.finalised += 1;
      return acc;
    },
    { total: 0, inProgress: 0, awaiting: 0, finalised: 0 },
  );

  const openCreate = () => {
    dispatch(getAllRequirements());
    dispatch(getAllUsers());
    setCreateOpen(true);
  };
  useCreateIntent(CREATE_INTENT.DESIGN, openCreate);

  const moveTo = (design, changes) => {
    dispatch(updateDesign({ payload: { id: design.id, ...changes } }));
    setSelected(null);
  };

  const finalised = selected?.finalisationStatus === FINAL.FINALISED;
  const exhausted = selected && selected.revisionAllowance > 0 && selected.revisionsUsed >= selected.revisionAllowance;

  return (
    <div className="flex-1 flex flex-col min-h-0">
      <Header title="Designs" breadcrumbs={[{ workspace: true }, { label: 'Designs' }]} />
      <main className="flex-1 overflow-auto p-6 pb-20 md:pb-6">
        <div className="grid grid-cols-4 gap-3 mb-6">
          <div className="bg-white rounded-lg border border-gray-100 p-4">
            <p className="text-[10px] text-gray-400 uppercase tracking-wide mb-1">Total Requests</p>
            <p className="text-[22px] font-bold text-gray-900">{stats.total}</p>
          </div>
          <div className="bg-blue-50 rounded-lg border border-blue-100 p-4">
            <p className="text-[10px] text-blue-600 uppercase tracking-wide mb-1">In Progress</p>
            <p className="text-[22px] font-bold text-blue-700">{stats.inProgress}</p>
          </div>
          <div className="bg-amber-50 rounded-lg border border-amber-100 p-4">
            <p className="text-[10px] text-amber-600 uppercase tracking-wide mb-1">Awaiting Brand</p>
            <p className="text-[22px] font-bold text-amber-700">{stats.awaiting}</p>
          </div>
          <div className="bg-green-50 rounded-lg border border-green-100 p-4">
            <p className="text-[10px] text-green-600 uppercase tracking-wide mb-1">Finalised</p>
            <p className="text-[22px] font-bold text-green-700">{stats.finalised}</p>
          </div>
        </div>

        <div className="flex items-center gap-3 mb-4 flex-wrap">
          <select value={brandFilter} onChange={(e) => setBrandFilter(e.target.value)} className="text-[12px] border border-gray-200 rounded-lg px-3 py-1.5 bg-white focus:outline-none">
            <option value="">All brands</option>
            {brands.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
          </select>
          {loading && <span className="text-[11px] text-gray-400">Loading…</span>}
          <div className="ml-auto flex gap-2">
            <button onClick={() => setUploadFor(null)} className="px-3 py-1.5 text-[12px] font-medium text-[#1b3a6b] border border-[#1b3a6b]/30 rounded-lg hover:bg-blue-50">Upload Version</button>
            <button onClick={openCreate} className="px-3 py-1.5 text-[12px] font-medium text-white bg-[#1b3a6b] rounded-lg hover:bg-[#152d55]">+ Create Request</button>
          </div>
        </div>

        {!loading && designs.length === 0 && <div className="bg-white rounded-lg border border-gray-100 py-12 text-center text-[12px] text-gray-400">No design requests</div>}

        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
          {designs.map((d) => <DesignCard key={d.id} design={d} onClick={() => !d.__optimistic && setSelected(d)} />)}
        </div>
      </main>

      {selected && (
        <div className="fixed inset-0 z-40 flex">
          <div className="flex-1 bg-black/30" onClick={() => setSelected(null)} />
          <div className="w-[480px] bg-white shadow-2xl overflow-y-auto">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 sticky top-0 bg-white z-10">
              <div>
                <h2 className="text-[14px] font-semibold text-gray-900">{selected.name}</h2>
                <p className="text-[11px] text-gray-400">{selected.requirementName}</p>
              </div>
              <button onClick={() => setSelected(null)} className="text-gray-400 hover:text-gray-600 text-xl">×</button>
            </div>
            <div className="p-6 space-y-5">
              {latestVersion(selected)?.frontImage && (
                <img src={latestVersion(selected).frontImage} alt={selected.name} className="w-full aspect-[3/4] object-cover rounded-lg" />
              )}

              <div className="flex gap-2 flex-wrap">
                <Badge status={label(DESIGN_STATUS, selected.status)} />
                <Badge status={label(CHARGE_STATUS, selected.chargeStatus)} />
                <Badge status={label(FINALISATION_STATUS, selected.finalisationStatus)} />
              </div>

              <div className="grid grid-cols-2 gap-3 text-[12px]">
                <div><p className="text-gray-400">Brand</p><p className="font-medium">{selected.brandName || '—'}</p></div>
                <div><p className="text-gray-400">Garment Type</p><p className="font-medium">{label(GARMENT_TYPE, selected.garmentType)}</p></div>
                <div><p className="text-gray-400">Designer</p><p className="font-medium">{selected.designerName || '—'}</p></div>
                <div><p className="text-gray-400">Current Version</p><p className="font-medium">{selected.currentVersion ? `V${selected.currentVersion}` : 'None yet'}</p></div>
                <div><p className="text-gray-400">Design Charge</p><p className="font-mono font-semibold">{formatINR(selected.charge)}</p></div>
                <div><p className="text-gray-400">Versions</p><p className="font-medium">{selected.versionCount ?? 0}</p></div>
              </div>

              <div>
                <div className="flex items-center justify-between text-[12px] mb-1">
                  <span className="font-medium text-gray-700">Revisions Used</span>
                  <span className={`font-semibold ${exhausted ? 'text-red-600' : 'text-gray-700'}`}>{selected.revisionsUsed ?? 0} / {selected.revisionAllowance ?? 0}</span>
                </div>
                <RevisionBar used={selected.revisionsUsed} allowed={selected.revisionAllowance} height="h-2" />
                {exhausted && (
                  <div className="mt-2 p-3 bg-red-50 border border-red-100 rounded-lg text-[12px] text-red-700">
                    Included revisions are exhausted. A new charge must be approved by the brand before more design work.
                  </div>
                )}
              </div>

              {selected.chargeStatus === CHARGE.PENDING && (
                <div className="p-3 bg-amber-50 border border-amber-100 rounded-lg">
                  <p className="text-[12px] font-semibold text-amber-800 mb-1">Design charge awaiting brand approval</p>
                  <p className="text-[11px] text-amber-700">Design work cannot begin until the brand approves the charge of {formatINR(selected.charge)}.</p>
                </div>
              )}

              {finalised && (
                <div className="p-3 bg-green-50 border border-green-100 rounded-lg">
                  <p className="text-[12px] font-semibold text-green-800">🔒 Finalised Design</p>
                  <p className="text-[11px] text-green-700 mt-0.5">This design is finalised. No further versions can be uploaded.</p>
                </div>
              )}

              {(selected.versions || []).length > 0 && (
                <div>
                  <p className="text-[11px] font-medium text-gray-500 uppercase tracking-wide mb-2">Version History</p>
                  <div className="space-y-2">
                    {selected.versions.map((v) => (
                      <div key={v.id} className="flex items-center gap-3 p-3 border border-gray-100 rounded-lg">
                        {v.frontImage ? <img src={v.frontImage} alt="" className="w-10 h-12 object-cover rounded" /> : <div className="w-10 h-12 bg-gray-100 rounded" />}
                        <div className="flex-1 min-w-0">
                          <p className="text-[12px] font-medium text-gray-800">V{v.versionNumber} · {formatDate(v.uploadedDate)}</p>
                          <p className="text-[11px] text-gray-500 truncate">{v.revisionSummary || '—'}</p>
                        </div>
                        <Badge status={label(DESIGN_VERSION_STATUS, v.status)} />
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {!finalised && (
                <div className="flex gap-2 flex-wrap pt-2">
                  {selected.chargeStatus === CHARGE.APPROVED && selected.status === STATUS.AWAITING_CHARGE && (
                    <button onClick={() => moveTo(selected, { status: STATUS.IN_PROGRESS, stage: 6, finalisationStatus: 2 })} className="flex-1 px-4 py-2.5 text-[12px] font-medium text-white bg-[#1b3a6b] rounded-lg hover:bg-[#152d55]">
                      Start Design Work
                    </button>
                  )}
                  {selected.chargeStatus === CHARGE.APPROVED && !exhausted && (
                    <button onClick={() => { setUploadFor(selected.id); setSelected(null); }} className="flex-1 px-4 py-2.5 text-[12px] font-medium text-[#1b3a6b] border border-[#1b3a6b]/30 rounded-lg hover:bg-blue-50">
                      Upload New Version
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <UploadVersionModal open={uploadFor !== undefined} initialDesignId={uploadFor} onClose={() => setUploadFor(undefined)} designs={designs} />
      <CreateDesignModal open={createOpen} onClose={() => setCreateOpen(false)} />
    </div>
  );
}
